"use client"

import { useState } from "react"
import { ArrowLeft, MailCheck } from "lucide-react"
import { requestPasswordResetAction } from "@/lib/actions/password-reset.action"
import { Logo } from "@/components/Logo"

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const form = new FormData(e.currentTarget)
    const email = form.get("email") as string

    const result = await requestPasswordResetAction({ email })

    if (result.ok) {
      setSent(true)
    } else {
      setError(result.error)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F4F6F9]">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Logo size="lg" />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#1B4F72]" />

          {sent ? (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <MailCheck className="w-6 h-6 text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Vérifiez votre boîte mail</h1>
              <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                Si un compte est associé à cette adresse, vous recevrez un email
                contenant un lien pour réinitialiser votre mot de passe. Le lien
                est valable 1 heure.
              </p>
              <a
                href="/login"
                className="inline-flex items-center gap-2 mt-6 text-sm text-[#1B4F72] hover:text-[#154360] hover:underline font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </a>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-900">Mot de passe oublié</h1>
                <p className="text-gray-500 text-sm mt-1">
                  Entrez votre email, nous vous enverrons un lien de réinitialisation.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoFocus
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4F72]/20 focus:border-[#1B4F72]/60 transition-all placeholder:text-gray-400"
                    placeholder="jean@exemple.fr"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-[#1B4F72] text-white rounded-lg text-sm font-medium hover:bg-[#154360] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? "Envoi..." : "Envoyer le lien"}
                </button>
              </form>

              <div className="text-center mt-5">
                <a
                  href="/login"
                  className="inline-flex items-center gap-2 text-sm text-[#1B4F72] hover:text-[#154360] hover:underline font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour à la connexion
                </a>
              </div>
            </>
          )}
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          Les comptes connectés via Google n&apos;ont pas de mot de passe :
          utilisez le bouton « Se connecter avec Google ».
        </p>
      </div>
    </div>
  )
}
